import { useEffect, useState } from "react";
import { api, formatApiError, fileSrc } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import PhotoUploader from "@/components/PhotoUploader";
import { eur } from "@/components/LineItemsEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingDown, Plus, Loader2, Trash2, FileDown } from "lucide-react";
import { toast } from "sonner";

const CATS = ["produits", "consommables", "materiel", "publicite", "assurance", "local", "electricite", "carburant", "logiciels", "autres"];

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), supplier: "", category: "produits", description: "", amount: "", payment_method: "", receipt: null });
  const [saving, setSaving] = useState(false);

  const load = async () => { setLoading(true); try { setExpenses((await api.get("/expenses")).data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount) || 0 });
      toast.success("Dépense enregistrée");
      setOpen(false); setForm({ date: new Date().toISOString().slice(0, 10), supplier: "", category: "produits", description: "", amount: "", payment_method: "", receipt: null });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/expenses/${id}`); toast.success("Dépense supprimée"); load(); };

  const total = expenses.reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <div>
      <PageHeader title="Dépenses" subtitle="Suivi de rentabilité — outil de gestion (non déductible du régime micro)" icon={TrendingDown}
        actions={<Button onClick={() => setOpen(true)} data-testid="btn-new-expense" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"><Plus className="w-4 h-4 mr-1.5" /> Nouvelle dépense</Button>} />

      <div className="mb-4 text-sm text-slate-500">Total dépenses : <span className="font-mono font-semibold text-red-600" data-testid="expenses-total">{eur(total)}</span></div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
        : expenses.length === 0 ? <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">Aucune dépense.</div>
        : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                  <tr><th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Fournisseur</th><th className="text-left px-4 py-3">Catégorie</th><th className="text-left px-4 py-3">Description</th><th className="text-right px-4 py-3">Montant</th><th className="px-4 py-3"></th></tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {expenses.map((e) => (
                    <tr key={e.id} className="hover:bg-slate-50" data-testid={`expense-row-${e.id}`}>
                      <td className="px-4 py-3 text-slate-500">{e.date}</td>
                      <td className="px-4 py-3 text-slate-700">{e.supplier}</td>
                      <td className="px-4 py-3 text-slate-500 capitalize">{e.category}</td>
                      <td className="px-4 py-3 text-slate-700">{e.description}</td>
                      <td className="px-4 py-3 text-right font-mono text-red-600">{eur(e.amount)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {e.receipt && <a href={fileSrc(e.receipt.url || e.receipt.path)} target="_blank" rel="noreferrer" className="p-2 rounded-lg hover:bg-slate-100 text-slate-600" title="Justificatif"><FileDown className="w-4 h-4" /></a>}
                          <button onClick={() => del(e.id)} data-testid={`expense-del-${e.id}`} className="p-2 rounded-lg hover:bg-red-500/10 text-red-600"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-md max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-slate-900">Nouvelle dépense</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="expense-date" className="bg-slate-50 border-slate-200" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Montant (€)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount" className="bg-slate-50 border-slate-200" /></div>
            <div className="space-y-1.5 col-span-2"><Label className="text-xs text-slate-500">Fournisseur</Label><Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} data-testid="expense-supplier" className="bg-slate-50 border-slate-200" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger data-testid="expense-category" className="bg-slate-50 border-slate-200 capitalize"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">{CATS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Moyen de paiement</Label><Input value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className="bg-slate-50 border-slate-200" /></div>
            <div className="space-y-1.5 col-span-2"><Label className="text-xs text-slate-500">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-slate-50 border-slate-200" /></div>
            <div className="col-span-2"><PhotoUploader label="Justificatif (photo / PDF)" value={form.receipt ? [form.receipt] : []} onChange={(arr) => setForm({ ...form, receipt: arr[arr.length - 1] || null })} testid="expense-receipt" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-save-expense" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
